import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Sparkles, ArrowRight } from "lucide-react";

const TARGET = new Date("2026-09-01T00:00:00");

function getRemaining() {
  const diff = TARGET.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    done: false,
  };
}

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="grid place-items-center min-w-[58px] sm:min-w-[68px] rounded-2xl bg-[#1B19A8] text-white font-display text-2xl sm:text-3xl font-bold py-3 tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
      <span className="mt-2 text-[10px] sm:text-xs uppercase tracking-[0.15em] text-[#1a1a1a]/55 font-semibold">{label}</span>
    </div>
  );
}

export default function ReminderPopup() {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState(getRemaining());

  useEffect(() => {
    if (sessionStorage.getItem("skz_reminder_seen")) return;
    const show = setTimeout(() => setOpen(true), 2600);
    return () => clearTimeout(show);
  }, []);

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setT(getRemaining()), 1000);
    return () => clearInterval(id);
  }, [open]);

  const close = useCallback(() => {
    sessionStorage.setItem("skz_reminder_seen", "1");
    setOpen(false);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] grid place-items-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="reminder-popup"
        >
          <div className="absolute inset-0 bg-[#0f0e5c]/60 backdrop-blur-sm" onClick={close} />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md rounded-3xl bg-white p-7 sm:p-9 shadow-[0_40px_100px_rgba(0,0,0,0.35)] overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#FFE500]/25 blur-2xl" />

            <button
              onClick={close}
              data-testid="reminder-close"
              aria-label="Kapat"
              className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full bg-[#f7f9fa] text-[#1a1a1a]/60 hover:bg-[#eef0f2] transition-colors"
            >
              <X size={18} />
            </button>

            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#FFE500] px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#1B19A8]">
                <Sparkles size={14} /> Kontenjanlar Doluyor
              </span>

              <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#1a1a1a] leading-tight mt-4">
                2026 Güz dönemini kaçırma!
              </h3>
              <p className="text-[#1a1a1a]/65 mt-2 leading-relaxed text-sm sm:text-base">
                {"Başvurular Temmuz–Ağustos'ta yoğunlaşıyor, öğretim yılı Eylül'ün ilk haftası başlıyor. Sınavsız kayıt ayrıcalığın için zaman azalıyor."}
              </p>

              <div className="flex items-center gap-1.5 text-[#1B19A8] font-semibold text-sm mt-6 mb-3">
                <Clock size={16} /> Eğitim başlangıcına kalan süre
              </div>

              {t.done ? (
                <div className="rounded-2xl bg-[#f7f9fa] p-4 text-center font-display text-lg font-bold text-[#1B19A8]">
                  Kayıtlar başladı! Hemen bilgi al.
                </div>
              ) : (
                <div className="flex justify-between gap-2">
                  <Unit value={t.days} label="Gün" />
                  <Unit value={t.hours} label="Saat" />
                  <Unit value={t.minutes} label="Dakika" />
                  <Unit value={t.seconds} label="Saniye" />
                </div>
              )}

              <a
                href="#basvuru"
                onClick={close}
                data-testid="reminder-cta"
                className="group mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1B19A8] px-6 py-4 text-base font-bold text-white hover:bg-[#141178] active:scale-[0.98] transition-[background-color,transform]"
              >
                Hemen Ücretsiz Bilgi Al
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <p className="text-center text-xs text-[#1a1a1a]/45 mt-3">Ücretsiz ve bağlayıcı değildir.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
